import React from 'react';

/**
 * WeatherBadge - Subtle weather indicator for calendar days
 * 
 * Design principles:
 * - Small and unobtrusive
 * - Gray text to not distract
 * - Shows rain warning subtly for pressure washing businesses
 * - If no data, renders nothing (graceful degradation)
 */
const WeatherBadge = ({ weather, compact = false }) => {
  // If no weather data, render nothing
  if (!weather) return null;

  const { icon, high, isRainy, precipChance } = weather;

  // Compact mode (for mobile or tight spaces)
  if (compact) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400">
        <span className="text-sm">{icon}</span>
        <span>{high}°</span>
      </span>
    );
  }

  // Full mode (for desktop day headers)
  return (
    <div className={`flex items-center gap-1 text-xs ${isRainy ? 'text-blue-400' : 'text-gray-400'}`}>
      <span className="text-sm">{icon}</span>
      <span>{high}°</span>
      {/* Show precip chance if significant */}
      {precipChance > 30 && (
        <span className="text-blue-400 ml-0.5">
          {precipChance}%
        </span>
      )}
    </div>
  );
};

/**
 * WeatherWarning - Shows a subtle warning for rainy days
 * Useful for pressure washing - don't want to schedule on rainy days!
 */
export const WeatherWarning = ({ weather }) => {
  if (!weather?.isRainy) return null;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-100 rounded text-xs text-blue-600">
      <span>{weather.icon}</span>
      <span>{weather.description} expected</span>
      {weather.precipChance > 0 && (
        <span className="text-blue-400">({weather.precipChance}% chance)</span>
      )}
    </div>
  );
};

export default WeatherBadge;